import { Link, NavLink } from 'react-router-dom';
import { ContinueAuditBanner } from '@/components/ContinueAuditBanner';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Menu, X, Shield, LogOut, Eye, EyeOff } from 'lucide-react';
import { useAdminStore } from '@/hooks/useAdminStore';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
  hideFooterCta?: boolean;
}

export function Layout({ children, hideFooterCta = false }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const headerOpacity = useTransform(scrollY, [0, 100], [0.8, 0.95]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const { isAuthenticated, logout, login } = useAdminStore();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleAdminClick = () => {
    if (isAuthenticated) {
      // Bereits eingeloggt, direkt zum Dashboard
      window.location.hash = ROUTE_PATHS.ADMIN;
    } else {
      // Passwort-Dialog anzeigen
      setShowPasswordDialog(true);
    }
  };

  const handleLogin = () => {
    const success = login(password);
    if (success) {
      setShowPasswordDialog(false);
      setPassword('');
      window.location.hash = ROUTE_PATHS.ADMIN;
    } else {
      alert('Falsches Passwort');
      setPassword('');
    }
  };

  const navItems: Array<{ label: string; path: string; icon?: typeof Shield }> = [
    { label: 'Home', path: ROUTE_PATHS.HOME },
    { label: 'Sales Engine', path: ROUTE_PATHS.SALES_ENGINE },
    { label: 'Über uns', path: ROUTE_PATHS.UEBER_UNS },
    { label: 'Kontakt', path: ROUTE_PATHS.KONTAKT },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <ContinueAuditBanner />
      <motion.header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-200"
        style={{
          backgroundColor: isScrolled ? 'var(--background)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(12px)' : 'none',
          boxShadow: isScrolled
            ? '0 4px 12px color-mix(in srgb, var(--primary) 8%, transparent)'
            : 'none',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-2">
              <img 
                src="/images/salesforge_logo.webp"
                alt="Salesforge Consulting" 
                className="h-14 w-auto object-contain"
              />
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `text-sm font-medium transition-colors hover:text-primary flex items-center gap-2 ${
                        isActive ? 'text-primary' : 'text-foreground/80'
                      }`
                    }
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    {item.label}
                  </NavLink>
                );
              })}
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAdminClick}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Button>
              )}
              {!isAuthenticated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAdminClick}
                  className="flex items-center gap-2 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  aria-label="Admin Login"
                >
                  <Shield className="w-3.5 h-3.5" />
                </Button>
              )}
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Abmelden
                </Button>
              )}
              <Button
                asChild
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Link to={ROUTE_PATHS.AUDIT}>Audit starten</Link>
              </Button>
            </nav>

            <button
              className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-card border-t border-border"
          >
            <nav className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `text-sm font-medium transition-colors hover:text-primary flex items-center gap-2 ${
                        isActive ? 'text-primary' : 'text-foreground/80'
                      }`
                    }
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    {item.label}
                  </NavLink>
                );
              })}
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleAdminClick();
                  }}
                  className="w-full justify-start flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Button>
              )}
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    logout();
                    setIsMenuOpen(false);
                  }}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full justify-start"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Abmelden
                </Button>
              )}
              <Button
                asChild
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
                onClick={() => setIsMenuOpen(false)}
              >
                <Link to={ROUTE_PATHS.AUDIT}>Audit starten</Link>
              </Button>
            </nav>
          </motion.div>
        )}
      </motion.header>

      <main className="flex-1 pt-16">{children}</main>

      {/* Footer CTA Banner */}
      {!hideFooterCta && <div className="bg-slate-950 border-t border-slate-800">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Kostenlos starten</p>
              <h3 className="text-xl font-bold text-white mb-1">Wo verliert Ihr Vertrieb heute Umsatz?</h3>
              <p className="text-sm text-slate-400">In 15 Minuten zur vollständigen Diagnose. Keine Anmeldung.</p>
            </div>
            <Button
              asChild
              className="shrink-0 bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/25 font-semibold px-8 h-12 rounded-xl"
            >
              <Link to={ROUTE_PATHS.AUDIT}>Jetzt Audit starten →</Link>
            </Button>
          </div>
        </div>
      </div>}

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <img 
                src="/images/salesforge_logo.webp"
                alt="Salesforge Consulting" 
                className="h-12 w-auto object-contain mb-4"
              />
              <p className="text-muted-foreground text-sm max-w-md">
                Ihr Partner für Sales Excellence. Wir helfen Unternehmen, ihre
                Vertriebsorganisation zu optimieren und nachhaltiges Wachstum zu
                erreichen.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Navigation</h3>
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Rechtliches</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={ROUTE_PATHS.IMPRESSUM}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Impressum
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.DATENSCHUTZ}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Datenschutz
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.AGB}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    AGB
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center">
            <p className="text-sm text-muted-foreground">
              © 2026 Salesforge. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </footer>

      {/* Admin Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Admin-Zugang
            </DialogTitle>
            <DialogDescription>
              Bitte geben Sie das Admin-Passwort ein
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-password">Passwort</Label>
              <div className="relative">
                <Input
                  id="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Passwort eingeben"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleLogin();
                    }
                  }}
                  className="pr-10"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPassword('');
                }}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleLogin}
                disabled={!password}
                className="flex-1"
              >
                Anmelden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
